/**
 * @file   src\Pages\PaxDeviceListing.tsx
 * @brief  Pax device listing page.
 * @date   APR, 2026
 */

import { React, useEffect, useState, Container, Table, Button, Modal, Form, useOutletContext, Pagination } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import AddIcon from '../assets/images/icons/addicon';
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';
import { AppContextType } from '../interfaces/generalInterface';
import { pageURLs } from '../utils/pageUrls';
import { MessageToaster } from '../utils/toastUtil';
import axios from '../config/axiosConfig';
import { EquipmentApi } from '../utils/apiUrls';
import { DeviceType } from '../utils/enums';
import SmallLoader from '../components/SmallLoader';

interface PaxDevice {
    id: number;
    name: string;
}

const PaxDeviceListing = () => {
    const { updateData } = useOutletContext<AppContextType>();
    const toaster = new MessageToaster();
    const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
    const [paxDeviceName, setPaxDeviceName] = useState('');
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
    const [showDeleteDeviceModal, setShowDeleteDeviceModal] = useState(false);
    const [selectedDeleteDeviceId, setSelectedDeleteDeviceId] = useState<number | null>(null);
    const [selectedDeleteDeviceName, setSelectedDeleteDeviceName] = useState('');

    const [paxDevices, setPaxDevices] = useState<PaxDevice[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const getPaxDevices = async (pageNo = 1) => {
        try {
            setLoading(true);
            const { data } = await axios.get(EquipmentApi.GET_DEVICES, {
                params: {
                    deviceType: DeviceType.PAX,
                    pageNo
                }
            });

            if (data?.error === 0) {
                const apiList = data?.data?.list || data?.data || [];
                if (Array.isArray(apiList)) {
                    const transformedList = apiList
                        .map((item: any) => ({
                            id: item?.WashBayDeviceID || item?.washBayDeviceID || item?.washBayDeviceId || item?.deviceId || 0,
                            name: item?.DeviceName || item?.deviceName || "N/A"
                        }))
                        .filter((item: PaxDevice) => item.id !== 0);
                    if (transformedList.length > 0) {
                        setPaxDevices(transformedList);
                        const totalCount = data?.data?.totalCount ?? transformedList.length;
                        const limit = (data?.data?.limit ?? transformedList.length) || 1;
                        setTotalPages(Math.ceil(totalCount / limit) || 1);
                    } else {
                        setPaxDevices([]);
                    }
                } else {
                    setPaxDevices([]);
                }
            } else {
                setPaxDevices([]);
                toaster.toastError(data?.message || "Failed to fetch devices.");
            }
            setLoading(false);
        } catch (error: any) {
            setPaxDevices([]);
            toaster.toastError(error?.response?.data?.message || error?.message || "Failed to fetch devices.");
            setLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(
            1,
            currentPage - Math.floor(totalPageNumbersToShow / 2)
        );
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    const handleOpenAddDeviceModal = () => {
        setPaxDeviceName('');
        setIsEditMode(false);
        setSelectedDeviceId(null);
        setShowAddDeviceModal(true);
    };

    const handleCloseAddDeviceModal = () => {
        setShowAddDeviceModal(false);
        setPaxDeviceName('');
        setIsEditMode(false);
        setSelectedDeviceId(null);
    };

    const handleEditDevice = async (deviceId: number) => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${EquipmentApi.GET_DEVICE_DETAILS}${deviceId}`);

            if (data?.error === 0) {
                const fetchedName = data?.data?.deviceName || data?.data?.DeviceName || '';
                setPaxDeviceName(fetchedName);
                setSelectedDeviceId(deviceId);
                setIsEditMode(true);
                setShowAddDeviceModal(true);
            } else {
                toaster.toastError(data?.message || "Failed to fetch device details.");
            }
        } catch (error: any) {
            toaster.toastError(error?.response?.data?.message || error?.message || "Failed to fetch device details.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDeleteDeviceModal = (deviceId: number, deviceName: string) => {
        setSelectedDeleteDeviceId(deviceId);
        setSelectedDeleteDeviceName(deviceName);
        setShowDeleteDeviceModal(true);
    };

    const handleCloseDeleteDeviceModal = () => {
        setSelectedDeleteDeviceId(null);
        setSelectedDeleteDeviceName('');
        setShowDeleteDeviceModal(false);
    };

    const handleDeleteDevice = async () => {
        if (selectedDeleteDeviceId === null) return;

        try {
            setLoading(true);
            const { data } = await axios.post(EquipmentApi.DELETE_DEVICE, {
                washBayDeviceId: selectedDeleteDeviceId
            });

            if (data?.error === 0) {
                toaster.toastSuccess(data?.message || "Device deleted successfully.");
                handleCloseDeleteDeviceModal();
                await getPaxDevices(currentPage);
            } else {
                toaster.toastError(data?.message || "Failed to delete device.");
            }
        } catch (error: any) {
            toaster.toastError(error?.response?.data?.message || error?.message || "Failed to delete device.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDevice = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmedDeviceName = paxDeviceName.trim();
        if (!trimmedDeviceName) return;

        try {
            setLoading(true);
            const requestParams = {
                washBayDeviceId: selectedDeviceId,
                deviceType: DeviceType.PAX,
                deviceName: trimmedDeviceName
            };
            const { data } = isEditMode
                ? await axios.post(EquipmentApi.EDIT_DEVICE, requestParams)
                : await axios.post(EquipmentApi.ADD_DEVICE, {
                    deviceType: DeviceType.PAX,
                    deviceName: trimmedDeviceName
                });

            if (data?.error === 0) {
                toaster.toastSuccess(data?.message || (isEditMode ? "Device updated successfully." : "Device added successfully."));
                handleCloseAddDeviceModal();
                await getPaxDevices(currentPage);
            } else {
                toaster.toastError(data?.message || (isEditMode ? "Failed to update device." : "Failed to add device."));
            }
        } catch (error: any) {
            toaster.toastError(error?.response?.data?.message || error?.message || (isEditMode ? "Failed to update device." : "Failed to add device."));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        updateData({
            heading: "Manage Paxdevice",
            url: `/superadmin/${pageURLs.pax_device_listing}`,
            showBackButton: false,
            showMainMenu: true,
            activeLink: "pax-device-listing"
        });
    }, []);

    useEffect(() => {
        getPaxDevices(currentPage);
    }, [currentPage]);

    return (
        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                <div className="manage-header-block d-flex justify-content-end">
                    <Button
                        type="button"
                        title="Add New Device"
                        variant="primary"
                        className="primary-btn d-flex align-items-center"
                        onClick={handleOpenAddDeviceModal}
                    >
                        Add New Device
                        <AddIcon />
                    </Button>
                </div>

                <div className="table-title-block d-flex align-items-center justify-content-between">
                    <h5 className='f-bold'>Manage Paxdevice</h5>
                </div>

                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paxDevices.length > 0 ? (
                                paxDevices.map((device) => (
                                    <tr key={device.id}>
                                        <td>{device.name}</td>
                                        <td className='nowrap'>
                                            <div className="active-group d-flex align-items-center">
                                                <Button title="Edit" variant="light" className="light-btn action-btn" onClick={() => handleEditDevice(device.id)}><EditIcon /></Button>
                                                <Button title="Delete" variant="light" className="light-btn action-btn" onClick={() => handleOpenDeleteDeviceModal(device.id, device.name)}><DeleteIcon /></Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td style={{ textAlign: 'center' }} colSpan={2}>No data found.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    {paxDevices.length ? (
                        <div className="mt-3 d-flex justify-content-end">
                            <Pagination>
                                <Pagination.First
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(1)}
                                />
                                <Pagination.Prev
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                />
                                {getVisiblePageNumbers().map((pageNumber) => (
                                    <Pagination.Item
                                        key={pageNumber}
                                        active={pageNumber === currentPage}
                                        onClick={() => handlePageChange(pageNumber)}
                                    >
                                        {pageNumber}
                                    </Pagination.Item>
                                ))}
                                <Pagination.Next
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                />
                                <Pagination.Last
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(totalPages)}
                                />
                            </Pagination>
                        </div>
                    ) : null}
                </div>
            </Container>
            <Modal show={showAddDeviceModal} onHide={handleCloseAddDeviceModal} centered className='add-modal'>
                <Modal.Body>
                    <h5 className='f-bold'>{isEditMode ? "Edit Device" : "Add Device"}</h5>
                    <form onSubmit={handleSaveDevice}>
                        <div className="form-group">
                            <p className="label">Pax Device Name</p>
                            <Form.Control
                                type="text"
                                placeholder="Enter pax device name"
                                value={paxDeviceName}
                                onChange={(event) => setPaxDeviceName(event.target.value)}
                                autoFocus
                            />
                        </div>
                        <div className="btn-sec d-grid">
                            <Button title="Cancel" variant="secondary" className='secondary-btn' onClick={handleCloseAddDeviceModal}>Cancel</Button>
                            <Button title="Save" variant="primary" className='primary-btn' type='submit' disabled={loading}>Save</Button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
            <Modal
                show={showDeleteDeviceModal}
                onHide={handleCloseDeleteDeviceModal}
                centered
                className='add-modal'
            >
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'>Do you want to delete the device {selectedDeleteDeviceName}?</h5>
                    <div className="btn-sec d-grid">
                        <Button variant="light" className="secondary-btn" onClick={handleCloseDeleteDeviceModal} title='Not Now'>Not Now</Button>
                        <Button variant="primary" className='primary-btn btn-main m-0' onClick={handleDeleteDevice} title='Yes' disabled={loading}>Yes</Button>
                    </div>
                </Modal.Body>
            </Modal>
            {loading && <SmallLoader />}
        </div>
    )
}

export default PaxDeviceListing;